// Localização: frontend/src/components/Financeiro/ModalTransferencia.jsx
// Modal para realizar transferências entre contas bancárias

import React, { useState, useEffect, useRef } from 'react';
import Modal from '../UI/Modal';
import PrimaryButton from '../Common/PrimaryButton';
import LoadingIndicator from '../Common/LoadingIndicator';
import { useContasBancarias } from '../../hooks/useFinanceiro';
import { ArrowRightLeft, Calendar, DollarSign, FileText } from 'lucide-react';

const ModalTransferencia = ({ isOpen, onClose, onConfirm, isLoading }) => {
    const [formData, setFormData] = useState({
        conta_origem_id: '',
        conta_destino_id: '',
        valor: '',
        data_transferencia: new Date().toISOString().split('T')[0],
        descricao: ''
    });
    const [errors, setErrors] = useState({});
    const hasLoadedContasRef = useRef(false);

    // Hook para buscar contas bancárias
    const { contas, fetchContas, loading: loadingContas } = useContasBancarias();

    useEffect(() => {
        if (isOpen) {
            // Carregar contas apenas uma vez quando o modal abrir
            if (!hasLoadedContasRef.current || contas.length === 0) {
                fetchContas().catch(err => {
                    console.error('[ModalTransferencia] Erro ao carregar contas bancárias:', err);
                });
                hasLoadedContasRef.current = true;
            }
            // Reset form
            setFormData({
                conta_origem_id: '',
                conta_destino_id: '',
                valor: '',
                data_transferencia: new Date().toISOString().split('T')[0],
                descricao: ''
            });
            setErrors({});
        } else {
            // Resetar flag quando o modal fechar
            hasLoadedContasRef.current = false;
        }
    }, [isOpen]); // Removido fetchContas das dependências

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value || 0);
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.conta_origem_id) {
            newErrors.conta_origem_id = 'Conta de origem é obrigatória';
        }

        if (!formData.conta_destino_id) {
            newErrors.conta_destino_id = 'Conta de destino é obrigatória';
        }

        if (formData.conta_origem_id && formData.conta_destino_id &&
            formData.conta_origem_id === formData.conta_destino_id) {
            newErrors.conta_destino_id = 'Conta de destino deve ser diferente da conta de origem';
        }

        if (!formData.valor || parseFloat(formData.valor) <= 0) {
            newErrors.valor = 'Valor deve ser maior que zero';
        }

        if (!formData.data_transferencia) {
            newErrors.data_transferencia = 'Data da transferência é obrigatória';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        const data = {
            ...formData,
            valor: parseFloat(formData.valor)
        };

        onConfirm(data);
    };

    // Encontrar contas selecionadas para exibir informações
    const contaOrigem = contas.find(c => c.id === parseInt(formData.conta_origem_id));
    const contaDestino = contas.find(c => c.id === parseInt(formData.conta_destino_id));

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Transferência entre Contas">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Conta de Origem */}
                <div>
                    <label htmlFor="conta_origem_id" className="block text-sm font-medium text-gray-700 mb-1">
                        <ArrowRightLeft className="inline h-4 w-4 mr-1" />
                        Conta de Origem <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="conta_origem_id"
                        name="conta_origem_id"
                        value={formData.conta_origem_id}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.conta_origem_id ? 'border-red-500' : 'border-gray-300'
                            }`}
                        disabled={loadingContas}
                    >
                        <option value="">Selecione a conta de origem...</option>
                        {contas.map(conta => (
                            <option key={conta.id} value={conta.id}>
                                {conta.nome} - {conta.banco} (Saldo: {formatCurrency(conta.saldo_atual || 0)})
                            </option>
                        ))}
                    </select>
                    {errors.conta_origem_id && (
                        <p className="mt-1 text-sm text-red-600">{errors.conta_origem_id}</p>
                    )}
                    {contaOrigem && (
                        <p className="mt-1 text-xs text-gray-500">
                            Saldo disponível: {formatCurrency(contaOrigem.saldo_atual || 0)}
                        </p>
                    )}
                </div>

                {/* Conta de Destino */}
                <div>
                    <label htmlFor="conta_destino_id" className="block text-sm font-medium text-gray-700 mb-1">
                        <ArrowRightLeft className="inline h-4 w-4 mr-1" />
                        Conta de Destino <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="conta_destino_id"
                        name="conta_destino_id"
                        value={formData.conta_destino_id}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.conta_destino_id ? 'border-red-500' : 'border-gray-300'
                            }`}
                        disabled={loadingContas}
                    >
                        <option value="">Selecione a conta de destino...</option>
                        {contas.map(conta => (
                            <option
                                key={conta.id}
                                value={conta.id}
                                disabled={conta.id === parseInt(formData.conta_origem_id)}
                            >
                                {conta.nome} - {conta.banco} (Saldo: {formatCurrency(conta.saldo_atual || 0)})
                            </option>
                        ))}
                    </select>
                    {errors.conta_destino_id && (
                        <p className="mt-1 text-sm text-red-600">{errors.conta_destino_id}</p>
                    )}
                    {contaDestino && (
                        <p className="mt-1 text-xs text-gray-500">
                            Saldo atual: {formatCurrency(contaDestino.saldo_atual || 0)}
                        </p>
                    )}
                </div>

                {/* Valor */}
                <div>
                    <label htmlFor="valor" className="block text-sm font-medium text-gray-700 mb-1">
                        <DollarSign className="inline h-4 w-4 mr-1" />
                        Valor da Transferência <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="number"
                        id="valor"
                        name="valor"
                        value={formData.valor}
                        onChange={handleChange}
                        step="0.01"
                        min="0.01"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.valor ? 'border-red-500' : 'border-gray-300'
                            }`}
                        placeholder="R$ 0,00"
                    />
                    {errors.valor && (
                        <p className="mt-1 text-sm text-red-600">{errors.valor}</p>
                    )}
                </div>

                {/* Data da Transferência */}
                <div>
                    <label htmlFor="data_transferencia" className="block text-sm font-medium text-gray-700 mb-1">
                        <Calendar className="inline h-4 w-4 mr-1" />
                        Data da Transferência <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        id="data_transferencia"
                        name="data_transferencia"
                        value={formData.data_transferencia}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.data_transferencia ? 'border-red-500' : 'border-gray-300'
                            }`}
                    />
                    {errors.data_transferencia && (
                        <p className="mt-1 text-sm text-red-600">{errors.data_transferencia}</p>
                    )}
                </div>

                {/* Descrição */}
                <div>
                    <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-1">
                        <FileText className="inline h-4 w-4 mr-1" />
                        Descrição (opcional)
                    </label>
                    <textarea
                        id="descricao"
                        name="descricao"
                        value={formData.descricao}
                        onChange={handleChange}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Adicione uma descrição para esta transferência..."
                    />
                </div>

                {/* Resumo da Transferência */}
                {formData.valor && contaOrigem && contaDestino && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Resumo da Transferência</h4>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-600">De:</span>
                                <span className="font-medium">{contaOrigem.nome}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Saldo atual:</span>
                                <span className="font-medium">{formatCurrency(contaOrigem.saldo_atual || 0)}</span>
                            </div>
                            <div className="flex justify-between text-red-600">
                                <span>Após transferência:</span>
                                <span className="font-bold">
                                    {formatCurrency((contaOrigem.saldo_atual || 0) - parseFloat(formData.valor))}
                                </span>
                            </div>
                            <div className="border-t border-blue-300 my-2"></div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Para:</span>
                                <span className="font-medium">{contaDestino.nome}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Saldo atual:</span>
                                <span className="font-medium">{formatCurrency(contaDestino.saldo_atual || 0)}</span>
                            </div>
                            <div className="flex justify-between text-green-600">
                                <span>Após transferência:</span>
                                <span className="font-bold">
                                    {formatCurrency((contaDestino.saldo_atual || 0) + parseFloat(formData.valor))}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        disabled={isLoading}
                    >
                        Cancelar
                    </button>
                    <PrimaryButton
                        type="submit"
                        disabled={isLoading}
                        className="px-4 py-2 flex items-center gap-2"
                    >
                        <ArrowRightLeft className="h-4 w-4" />
                        {isLoading ? 'Transferindo...' : 'Confirmar Transferência'}
                    </PrimaryButton>
                </div>
            </form>
        </Modal>
    );
};

export default ModalTransferencia;
